import { defineStore } from 'pinia';
import { ref } from 'vue';

import type {
  TrackerCreateDTO,
  TrackerDetailResultDTO,
  TrackerResultDTO,
  TrackerTemplateCreateDTO,
  TrackerTemplateResultDTO,
  TrackerUpdateDTO,
  TrackerWebhookStatusResultDTO,
  TypesenseSearchResponseDTO,
} from '@nora-monorepo/nora-api-client';
import { useApiStore } from '@nora-monorepo/ui-shared';

export const useAdminTrackerStore = defineStore('adminTrackerStore', () => {
  const api = useApiStore();

  const pageSize = 20;

  const count = ref<number>(0);
  const trackers = ref<TrackerResultDTO[]>([]);

  const showTrackerForm = ref(false);
  const canLoadMoreTrackers = ref(true);
  const trackersPage = ref(0);
  const isLoadingTrackers = ref(false);

  const isEditingTracker = ref(false);

  async function loadTrackersCount(): Promise<void> {
    const response = await api.unwrap(
      api.tracker.countTrackers(),
    );

    if (response.data) {
      count.value = response.data.count;
    }
  }

  async function searchTrackers(query: string): Promise<TypesenseSearchResponseDTO | undefined> {
    try {
      isLoadingTrackers.value = true;

      const response = await api.unwrap(
        api.tracker.searchTrackers({
          query,
          limit: pageSize,
          skip: trackersPage.value * pageSize,
        }),
      );

      return response.data;
    }
    catch (error) {
      console.error(error);
    }
    finally {
      isLoadingTrackers.value = false;
    }

    return undefined;
  }

  async function loadTrackersData(trackerQuery = ''): Promise<void> {
    try {
      const trackersData = await searchTrackers(trackerQuery);

      if (!trackersData) {
        return;
      }

      trackers.value.push(
        ...(trackersData?.hits?.map(
          hit => hit.document as TrackerResultDTO,
        ) ?? []),
      );

      trackersPage.value++;

      if (
        trackersData.found === 0
        || trackers.value.length >= trackersData.found
      ) {
        canLoadMoreTrackers.value = false;
      }
    }
    catch (error) {
      console.error(error);
    }
  }

  async function loadTrackerDetails(trackerId: string): Promise<TrackerDetailResultDTO | undefined> {
    try {
      const response = await api.unwrap(
        api.tracker.getTrackerById({ id: trackerId }),
      );

      if (!response.data) {
        return;
      }

      return response.data;
    }
    catch (error) {
      console.error(error);
    }

    return undefined;
  }

  async function createTracker(trackerCreateDTO: TrackerCreateDTO): Promise<TrackerResultDTO | undefined> {
    try {
      const tracker = await api.unwrap(
        api.tracker.createTracker({
          trackerCreateDTO,
        }),
      );

      if (tracker.data) {
        trackers.value.unshift(tracker.data);
        count.value++;

        return tracker.data;
      }
    }
    catch (error) {
      console.error(error);
    }

    return undefined;
  }

  async function updateTracker(trackerId: string, tracker: TrackerUpdateDTO): Promise<TrackerResultDTO | undefined> {
    const response = await api.unwrap(
      api.tracker.updateTracker({ id: trackerId, trackerUpdateDTO: tracker }),
    );

    if (!response.data) {
      return;
    }

    const index = trackers.value.findIndex(existingTracker => existingTracker.id === trackerId);
    if (index !== -1) {
      trackers.value[index] = response.data;
    }

    return response.data;
  }

  async function createTemplate(trackerTemplateCreateDTO: TrackerTemplateCreateDTO): Promise<TrackerTemplateResultDTO | undefined> {
    try {
      const response = await api.unwrap(
        api.trackerTemplate.createTemplate({
          trackerTemplateCreateDTO,
        }),
      );

      return response.data;
    }
    catch (error) {
      console.error(error);
    }

    return undefined;
  }

  async function updateTemplate(trackerTemplateId: string, trackerTemplateUpdateDTO: TrackerTemplateCreateDTO): Promise<void> {
    try {
      await api.unwrap(
        api.trackerTemplate.updateTemplate({
          id: trackerTemplateId,
          trackerTemplateUpdateDTO,
        }),
      );
    }
    catch (error) {
      console.error(error);
    }
  }

  async function deleteTemplate(trackerTemplateId: string): Promise<void> {
    try {
      await api.unwrap(
        api.trackerTemplate.deleteTemplate({
          id: trackerTemplateId,
        }),
      );
    }
    catch (error) {
      console.error(error);
    }
  }

  async function listTemplates(): Promise<TrackerTemplateResultDTO[] | undefined> {
    try {
      const response = await api.unwrap(
        api.trackerTemplate.listTemplates(),
      );

      return response.data;
    }
    catch (error) {
      console.error(error);
    }

    return undefined;
  }

  async function getTemplateById(trackerTemplateId: string): Promise<TrackerTemplateResultDTO | undefined> {
    try {
      const response = await api.unwrap(
        api.trackerTemplate.getTemplateById({
          id: trackerTemplateId,
        }),
      );

      return response.data;
    }
    catch (error) {
      console.error(error);
    }

    return undefined;
  }

  async function getTrackerWebhookStatus(trackerId: string): Promise<TrackerWebhookStatusResultDTO | undefined> {
    try {
      const response = await api.unwrap(
        api.tracker.getWebhookStatusById({
          id: trackerId,
        }),
      );

      return response.data;
    }
    catch (error) {
      console.error(error);
    }

    return undefined;
  }

  async function deleteTracker(trackerId: string): Promise<void> {
    try {
      await api.unwrap(
        api.tracker.deleteTracker({
          id: trackerId,
        }),
      );

      trackers.value = trackers.value.filter(tracker => tracker.id !== trackerId);
    }
    catch (error) {
      console.error(error);
    }
  }

  async function eventDeleteTracker(trackerId: string): Promise<void> {
    trackers.value = trackers.value.filter(tracker => tracker.id !== trackerId);
    count.value--;
  }

  async function reactivateTracker(trackerId: string): Promise<TrackerWebhookStatusResultDTO | undefined> {
    try {
      const response = await api.unwrap(
        api.tracker.reactivateTrackerWebhook({
          id: trackerId,
        }),
      );

      return response.data;
    }
    catch (error) {
      console.error(error);

      return undefined;
    }
  }

  async function sendTestDataToWebhook(id: string, body: string): Promise<void> {
    try {
      await api.unwrap(
        api.tracker.sendTestDataToWebhook({
          id,
          body,
        }),
      );
    }
    catch (error) {
      console.error(error);
    }
  }

  function reset(): void {
    trackers.value = [];
    canLoadMoreTrackers.value = true;
    trackersPage.value = 0;
    isLoadingTrackers.value = false;
  }

  function setShowTrackerForm(value: boolean): void {
    showTrackerForm.value = value;
  }

  return {
    count,
    trackers,
    showTrackerForm,
    canLoadMoreTrackers,
    isLoadingTrackers,
    isEditingTracker,

    loadTrackersCount,
    loadTrackersData,
    loadTrackerDetails,
    reset,
    setShowTrackerForm,
    createTracker,
    updateTracker,
    deleteTracker,
    eventDeleteTracker,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    listTemplates,
    getTemplateById,
    getTrackerWebhookStatus,
    reactivateTracker,
    sendTestDataToWebhook,
  };
});
